import { dogNames, DogName, armorMetadata, colorInventory, distanceInventory, surfaceInventory, weatherInventory, overrideInventory } from '../inventories';

export interface BoostAttribute {
  display_type: 'boost_number' | 'boost_percentage';
  trait_type: string;
  value: number;
}

export class GeneticsModel {
  sex: 'Male' | 'Female';
  color: [number, number, number, number];
  armor: [number, number, number, number];
  head: [number, number, number, number];
  legsBack: [number, number, number, number];
  legsFront: [number, number, number, number];
  pawsBack: [number, number, number, number];
  pawsFront: [number, number, number, number];
  tail: [number, number, number, number];

  fatigueFactor: [number, number, number, number];
  surfacePreference: [number, number, number, number, number, number, number, number, number, number];
  distancePreference: [number, number, number, number, number, number, number, number, number, number];
  weatherPreference: [number, number, number, number, number, number, number, number, number, number];
  wildCard: [number, number, number, number];

  geneticSequence: string;

  traitInventory?: TraitInventory;

  constructor(geneticSequence: string = undefined) {
    if(geneticSequence) {
      this.assignTraits(this.decodeSequence(geneticSequence));
    } else {
      this.assignTraits(this.decodeSequence('000000000000000000000000000000000000000000000000000000'));
    }
  }

  assignTraits(genes: Partial<GeneticsModel>) {
    this.sex = genes.sex ? genes.sex : 'Male';
    this.color = genes.color ? genes.color : [0, 0, 0, 0];
    this.armor = genes.armor ? genes.armor : [0, 0, 0, 0];
    this.head = genes.head ? genes.head : [0, 0, 0, 0];
    this.legsBack = genes.legsBack ? genes.legsBack : [0, 0, 0, 0];
    this.legsFront = genes.legsFront ? genes.legsFront : [0, 0, 0, 0];
    this.pawsBack = genes.pawsBack ? genes.pawsBack : [0, 0, 0, 0];
    this.pawsFront = genes.pawsFront ? genes.pawsFront : [0, 0, 0, 0];
    this.tail = genes.tail ? genes.tail : [0, 0, 0, 0];
    this.fatigueFactor = genes.fatigueFactor ? genes.fatigueFactor : [0, 0, 0, 0];
    this.surfacePreference = genes.surfacePreference ? genes.surfacePreference : [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    this.distancePreference = genes.distancePreference ? genes.distancePreference : [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    this.weatherPreference = genes.weatherPreference ? genes.weatherPreference : [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    this.wildCard = genes.wildCard ? genes.wildCard : [0, 0, 0, 0];

    return this;
  }

  encodeSequence() {
    let geneSeq = '';

    geneSeq = geneSeq + this.sex == 'Male' ? '1' : '2';
    geneSeq = geneSeq + this.color.join('');
    geneSeq = geneSeq + this.armor.join('');
    geneSeq = geneSeq + this.head.join('');
    geneSeq = geneSeq + this.legsBack.join('');
    geneSeq = geneSeq + this.legsFront.join('');
    geneSeq = geneSeq + this.pawsBack.join('');
    geneSeq = geneSeq + this.pawsFront.join('');
    geneSeq = geneSeq + this.tail.join('');
    geneSeq = geneSeq + this.fatigueFactor.join('');
    geneSeq = geneSeq + this.surfacePreference.join('');
    geneSeq = geneSeq + this.distancePreference.join('');
    geneSeq = geneSeq + this.weatherPreference.join('');
    geneSeq = geneSeq + this.wildCard.join('');

    geneSeq = '0'.concat(geneSeq); // Telomere cap

    return geneSeq;
  }

  /**
   * 
   * @param geneSeq Hound Genetic Sequence
   * 000000000000000000000000000000000000000000000000000000000000000000000000000000
   * 0t 0s 0000c 00 00 00 00h    00 00 00 00e     00 00 00 00p    00 00 00 00s    00 00 00 00a     00 00 00 00t     0 0 0 0   0 0 0 0   0 0 0 0   0 0 0 0   00000000 

   * 0            2              0 0 0 0   00 00 00 00    00 00 00 00     00 00 00 00    00 00 00 00    00 00 00 00     00 00 00 00     0 0 0 0        0 0 0 0              0 0 0 0            0 0 0 0             00000000   
   * Telomere     1 = M / 2 = F  color       head            eye           paw             skin           armor            tail      fatigue factor  surface preference  distance preference  weather preference  wild card   
   * 
   */
  decodeSequence(geneSeq: string) {
    this.geneticSequence = geneSeq;

    let genes: Partial<GeneticsModel> = {
      color: [0, 0, 0, 0],
      armor: [0, 0, 0, 0],
      head: [0, 0, 0, 0],
      legsBack: [0, 0, 0, 0],
      legsFront: [0, 0, 0, 0],
      pawsBack: [0, 0, 0, 0],
      pawsFront: [0, 0, 0, 0],
      tail: [0, 0, 0, 0],
      fatigueFactor: [0, 0, 0, 0],
      surfacePreference: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      distancePreference: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      weatherPreference: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      wildCard: [0, 0, 0, 0]
    };
    
    genes.sex = geneSeq.slice(1, 2) == '1' ? 'Male' : 'Female';

    genes.color[0] = Number(geneSeq.slice(2, 3));
    genes.color[1] = Number(geneSeq.slice(3, 4));
    genes.color[2] = Number(geneSeq.slice(4, 5));
    genes.color[3] = Number(geneSeq.slice(5, 6));

    genes.armor[0] = Number(geneSeq.slice(6, 7));
    genes.armor[1] = Number(geneSeq.slice(7, 8));
    genes.armor[2] = Number(geneSeq.slice(8, 9));
    genes.armor[3] = Number(geneSeq.slice(9, 10));

    genes.head[0] = Number(geneSeq.slice(10, 11));
    genes.head[1] = Number(geneSeq.slice(11, 12));
    genes.head[2] = Number(geneSeq.slice(12, 13));
    genes.head[3] = Number(geneSeq.slice(13, 14));

    genes.legsBack[0] = Number(geneSeq.slice(14, 15));
    genes.legsBack[1] = Number(geneSeq.slice(15, 16));
    genes.legsBack[2] = Number(geneSeq.slice(16, 17));
    genes.legsBack[3] = Number(geneSeq.slice(17, 18));

    genes.legsFront[0] = Number(geneSeq.slice(18, 19));
    genes.legsFront[1] = Number(geneSeq.slice(19, 20));
    genes.legsFront[2] = Number(geneSeq.slice(20, 21));
    genes.legsFront[3] = Number(geneSeq.slice(21, 22));

    genes.pawsBack[0] = Number(geneSeq.slice(22, 23));
    genes.pawsBack[1] = Number(geneSeq.slice(23, 24));
    genes.pawsBack[2] = Number(geneSeq.slice(24, 25));
    genes.pawsBack[3] = Number(geneSeq.slice(25, 26));

    genes.pawsFront[0] = Number(geneSeq.slice(26, 27));
    genes.pawsFront[1] = Number(geneSeq.slice(27, 28));
    genes.pawsFront[2] = Number(geneSeq.slice(28, 29));
    genes.pawsFront[3] = Number(geneSeq.slice(29, 30));
    
    genes.tail[0] = Number(geneSeq.slice(30, 31));
    genes.tail[1] = Number(geneSeq.slice(31, 32));
    genes.tail[2] = Number(geneSeq.slice(32, 33));
    genes.tail[3] = Number(geneSeq.slice(33, 34));


    genes.fatigueFactor[0] = Number(geneSeq.slice(34, 35));
    genes.fatigueFactor[1] = Number(geneSeq.slice(35, 36));
    genes.fatigueFactor[2] = Number(geneSeq.slice(36, 37));
    genes.fatigueFactor[3] = Number(geneSeq.slice(37, 38));

    genes.surfacePreference[0] = Number(geneSeq.slice(38, 39));
    genes.surfacePreference[1] = Number(geneSeq.slice(39, 40));
    genes.surfacePreference[2] = Number(geneSeq.slice(40, 41));
    genes.surfacePreference[3] = Number(geneSeq.slice(41, 42));
    genes.surfacePreference[4] = Number(geneSeq.slice(42, 43));
    genes.surfacePreference[5] = Number(geneSeq.slice(43, 44));
    genes.surfacePreference[6] = Number(geneSeq.slice(44, 45));
    genes.surfacePreference[7] = Number(geneSeq.slice(45, 46));
    genes.surfacePreference[8] = Number(geneSeq.slice(46, 47));
    genes.surfacePreference[9] = Number(geneSeq.slice(47, 48));

    genes.distancePreference[0] = Number(geneSeq.slice(48, 49)); 
    genes.distancePreference[1] = Number(geneSeq.slice(49, 50)); 
    genes.distancePreference[2] = Number(geneSeq.slice(50, 51)); 
    genes.distancePreference[3] = Number(geneSeq.slice(51, 52));
    genes.distancePreference[4] = Number(geneSeq.slice(52, 53)); 
    genes.distancePreference[5] = Number(geneSeq.slice(53, 54)); 
    genes.distancePreference[6] = Number(geneSeq.slice(54, 55)); 
    genes.distancePreference[7] = Number(geneSeq.slice(55, 56)); 
    genes.distancePreference[8] = Number(geneSeq.slice(56, 57)); 
    genes.distancePreference[9] = Number(geneSeq.slice(57, 58));

    genes.weatherPreference[0] = Number(geneSeq.slice(58, 59));
    genes.weatherPreference[1] = Number(geneSeq.slice(59, 60));
    genes.weatherPreference[2] = Number(geneSeq.slice(60, 61));
    genes.weatherPreference[3] = Number(geneSeq.slice(61, 62));
    genes.weatherPreference[4] = Number(geneSeq.slice(62, 63));
    genes.weatherPreference[5] = Number(geneSeq.slice(63, 64));
    genes.weatherPreference[6] = Number(geneSeq.slice(64, 65));
    genes.weatherPreference[7] = Number(geneSeq.slice(65, 66));
    genes.weatherPreference[8] = Number(geneSeq.slice(66, 67));
    genes.weatherPreference[9] = Number(geneSeq.slice(67, 68));

    genes.wildCard[0] = Number(geneSeq.slice(68, 69));
    genes.wildCard[1] = Number(geneSeq.slice(69, 70));
    genes.wildCard[2] = Number(geneSeq.slice(70, 71));
    genes.wildCard[3] = Number(geneSeq.slice(71, 72));

    return genes;
  }

  sequenceAsArray() {
    return Array.from(this.geneticSequence).map(c => Number(c));
  }

  /**
   * 
   * @param geneExpression Actual gene value
   * @param xmin Minimum Input
   * @param xmax Maximum Input
   * @param ymin Minimum Output, smallest named trait file in assets folder
   * @param ymax Maximum Output, largest named trait file in assets folder
   * @returns Discrete file names that actually exist in our trait inventory
   * @deprecated
   */
  geneLinearInterpolation(geneExpression: string, ymax = 10, xmax = 99, ymin = 0,  xmin = 0) {
    return Math.round(ymin + (Number(geneExpression) - xmin)*(ymax - ymin)/(xmax - xmin));
  }

  /**
   * 
   * @param geneExpression Actual gene value
   * @param ymax Maximum Output, largest named trait file in assets folder
   * @returns Discrete file names that actually exist in our trait inventory
   */
  geneCap(geneExpression: number, ymax = 10) {
    return geneExpression > ymax ? ymax : Number(geneExpression);
  }

  armorMetadata() {
    return {
      armor: armorMetadata[this.armor[3]],
      head: armorMetadata[this.head[3]],
      legsBack: armorMetadata[this.legsBack[3]],
      legsFront: armorMetadata[this.legsFront[3]],
      pawsBack: armorMetadata[this.pawsBack[3]],
      pawsFront: armorMetadata[this.pawsFront[3]],
      tail: armorMetadata[this.tail[3]]
    };
  }

  mainColor() {
    return colorInventory[this.color[3]];
  }

  neonColor() {
    return colorInventory[this.color[2]];
  }

  houndDescription(extensionTraits: string = null) {
    let description = `Houndrace is a P2E Blockchain game where players compete with each other in exciting racing arenas and in a true multi-chain and multi-asset environment.\n\nRace your winner, breed new generations and compete in exciting tournaments with other players. Join the Houndverse today!`;
    if (extensionTraits) {
      const extensionTrait = overrideInventory.find(override => override.value === extensionTraits);
      if (extensionTraits && extensionTrait.houndDescriptionPrepend) description = `${extensionTrait.houndDescriptionPrepend}\n\n${description}`;
    }
    
    return description;
  }

  boostAttributes(): BoostAttribute[] {
    const result: BoostAttribute[] = [];
    for (let i = 0; i < this.surfacePreference.length; i++) {
      result.push({
        display_type: 'boost_percentage',
        trait_type: `Surface Preference (${surfaceInventory[i]})`,
        value: (this.surfacePreference[i] + 1) * 10,
      });
    }
    for (let i = 0; i < this.distancePreference.length; i++) {
      result.push({
        display_type: 'boost_percentage',
        trait_type: `Distance Preference (${distanceInventory[i]})`,
        value: (this.distancePreference[i] + 1) * 10,
      });
    }
    for (let i = 0; i < this.weatherPreference.length; i++) {
      result.push({
        display_type: 'boost_percentage',
        trait_type: `Weather Preference (${weatherInventory[i]})`,
        value: (this.weatherPreference[i] + 1) * 10,
      });
    }
    return result;
  }

  randomName(): DogName {
    const dogName = dogNames[~~(Math.random() * dogNames.length)];
    return (dogName.gender == this.sex || dogName.gender == 'Unisex') ? dogName : this.randomName();
  }

}

export interface TraitInventory {
  headsMaxIndex?: number;
  eyesMaxIndex?: number;
  pawsMaxIndex?: number;
  skinsMaxIndex?: number;
  armorsMaxIndex?: number;
  tailsMaxIndex?: number;
}
